import * as Sentry from "@sentry/react"
import { useDispatch } from "react-redux"
import { MyDispatch, MySelector } from "../redux/store"
import { toast } from "sonner"
import {
  filterFileAndMove,
  filterGallery,
  filterActiveAlbumFiles,
  getActiveAlbumFiles,
  getAllFiles,
  getPhysicalAlbums,
} from "../redux/gallerySlice"
import {
  deleteFileFromEvent,
  moveFileToFolder,
  updateFilesInPhysicalAlbum,
  getFileProxyDownloadUrl,
} from "../api"
import {
  resetSelectedPhotos,
  setAddToAlbumArray,
  setCurrentFolderData,
  setShareFile,
  setIndividualProgress,
  setShowDownloadModal,
  setShowDownloadAllModal,
  setDownloadAllFileIds,
} from "../redux/utilSlice"
import { NIL_UUID } from "../types/physicalAlbum"
import { saveAs } from "file-saver"
import { generateWatermarkedImage, isImage } from "../utils/watermarkGenerator"
import { checkVideo } from "../utils/isVideo"
import { getOptimalUrl, MediaFile } from "../utils/getOptimalUrl"
import { isAndroidWebView, isIOS, isWebView, isWhatsAppWebView } from "../utils/webViewDetect"

export const HandleAsyncFns = () => {
  const dispatch = useDispatch<MyDispatch>()

  const { eventId, userId, data: eventData } = MySelector(
    (state) => state.event
  )
  const {
    type,
    shareFile: shareLink,
    currentFolderData,
    currentFolder,
    activeAlbumId,
  } = MySelector((state) => state.util)

  // Subscription events authorize by subscriptionId; fall back to a nil UUID
  // when no host userId is stored so the (UUID-parsing) backend still accepts it.
  const effectiveUserId = userId || NIL_UUID

  // Chunk size state removed - no longer needed for individual downloads

  const deleteFile = async (photos: Array<string>) => {
    toast.success(
      `${photos?.length > 1 ? `${photos.length} Files` : "File"} ${
        type === "gallery" ? "Deleted" : "Removed"
      } Successfully`
    )

    if (type === "gallery") {
      const inCaseOfErrorRef = currentFolderData

      try {
        dispatch(filterGallery(photos))

        dispatch(
          setCurrentFolderData(
            Array.isArray(currentFolderData)
              ? currentFolderData.filter((cf) => !photos.includes(cf.id))
              : []
          )
        )

        const res = await deleteFileFromEvent({
          fileIds: photos,
        })

        if (res?.status !== "OK") {
          toast.error(res?.error || "Error Deleting File")
          dispatch(getAllFiles({ eventId: eventId || "" }))
          dispatch(setCurrentFolderData(inCaseOfErrorRef))
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { action: "delete_file" } })
        toast.error("Error Deleting File")
        dispatch(getAllFiles({ eventId: eventId || "" }))
        dispatch(setCurrentFolderData(inCaseOfErrorRef))
      } finally {
        dispatch(resetSelectedPhotos())
      }
    }

    if (type === "physical") {
      if (!activeAlbumId) {
        toast.error("No album selected")
        dispatch(resetSelectedPhotos())
        return
      }
      try {
        dispatch(filterActiveAlbumFiles(photos))

        const res = await updateFilesInPhysicalAlbum({
          userId: effectiveUserId,
          physicalAlbumId: activeAlbumId,
          fileIds: photos,
          addToPhysicalAlbum: false,
        })
        if (res?.status !== "OK") {
          toast.error("Error Removing File From Physical Album")
          dispatch(
            getActiveAlbumFiles({
              physicalAlbumId: activeAlbumId,
              userId: userId || undefined,
            })
          )
        }
        // refresh counts / membership across albums
        dispatch(getPhysicalAlbums({ eventId: eventId || "", userId: userId || undefined }))
      } catch (error) {
        Sentry.captureException(error, { tags: { action: "remove_from_album" } })
        toast.error("Error Removing File From Physical Album")
        dispatch(getActiveAlbumFiles({ physicalAlbumId: activeAlbumId, userId: userId || undefined }))
      } finally {
        dispatch(resetSelectedPhotos())
      }
    }
  }

  const moveFile = async (folderId: string, photos: Array<string>) => {
    const inCaseOfErrorRef = currentFolderData

    try {
      dispatch(filterFileAndMove({ folderId, photos }))

      currentFolder === "custom" &&
        dispatch(
          setCurrentFolderData(
            Array.isArray(currentFolderData)
              ? currentFolderData.filter((cf) => !photos.includes(cf.id))
              : []
          )
        )

      const res = await moveFileToFolder({ folderId, fileIds: photos })

      if (res?.status !== "OK") {
        toast.error(res?.error || "Error Deleting File")
        dispatch(getAllFiles({ eventId: eventId || "" }))
        dispatch(setCurrentFolderData(inCaseOfErrorRef))
      } else
        toast.success(
          `${
            photos?.length > 1 ? `${photos.length} Files` : "File"
          } Moved Successfully`
        )
    } catch (error) {
      Sentry.captureException(error, { tags: { action: "move_file" } })
      toast.error("Error Moving Files")
      dispatch(getAllFiles({ eventId: eventId || "" }))
      dispatch(setCurrentFolderData(inCaseOfErrorRef))
    } finally {
      dispatch(resetSelectedPhotos())
    }
  }

  // Opens a URL in Chrome on Android via the Intent URL scheme.
  // Falls back to window.location.href if the intent is not handled.
  const openInChrome = (url: string) => {
    const stripped = url.replace(/^https?:\/\//, "")
    window.location.href = `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;end`
  }

  const downloadIndividualFiles = async (files: Array<MediaFile & { name: string; id?: string }>) => {
    const filesRef = files
    dispatch(resetSelectedPhotos())
    dispatch(setShowDownloadModal(true))

    // iOS + multiple files: collect all blobs first, then share in one sheet.
    const iosMultiBatch = isIOS() &&
      filesRef.length > 1 &&
      typeof navigator.share === "function" &&
      typeof navigator.canShare === "function"
    const batchedShareFiles: File[] = []

    try {
      dispatch(setIndividualProgress({
        currentFile: 0,
        totalFiles: filesRef.length,
        currentFileProgress: 0,
        currentFileName: '',
      }))

      for (let i = 0; i < filesRef.length; i++) {
        const fileToDownload = filesRef[i]
        const downloadUrl = eventData?.allowOriginalDownload ?
          getOptimalUrl(fileToDownload, false) :
          getOptimalUrl(fileToDownload, true)

        dispatch(setIndividualProgress({
          currentFile: i + 1,
          currentFileName: fileToDownload.name,
          currentFileProgress: 0,
        }))

        // For Android WebView (WhatsApp, Instagram, etc.): skip fetch entirely.
        // fetch() fails CORS in WebView; instead open the proxy endpoint in
        // Chrome via the Android intent URL scheme so Chrome handles the download.
        // isWhatsAppWebView() matches iOS too (WhatsApp sets UA on all platforms),
        // so scope the check to Android explicitly.
        const ua = navigator.userAgent
        const inAndroidWebView = isAndroidWebView() || (/Android/i.test(ua) && /whatsapp/i.test(ua))
        if (inAndroidWebView) {
          const attachUrl = fileToDownload.id ? getFileProxyDownloadUrl(fileToDownload.id) : downloadUrl
          openInChrome(attachUrl)
          dispatch(setIndividualProgress({ currentFileProgress: 100 }))
          if (i < filesRef.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 800))
          }
          continue
        }

        try {
          let finalBlob: Blob
          let fileName = fileToDownload.name

          // Check if watermark should be applied
          if (eventData?.watermarkEnabled && eventData?.watermarkText && isImage(downloadUrl) && !checkVideo(downloadUrl)) {
            // Generate watermarked image with progress tracking
            finalBlob = await generateWatermarkedImage(downloadUrl, eventData.watermarkText)
            // Ensure filename has proper extension
            if (!fileName.toLowerCase().includes('.jpg') && !fileName.toLowerCase().includes('.jpeg')) {
              fileName = fileName.replace(/\.[^/.]+$/, '') + '.jpg'
            }
            dispatch(setIndividualProgress({ currentFileProgress: 50 }))
          } else {
            // Download original file with progress tracking.
            // cache: 'no-store' avoids reusing the gallery <img>'s cached
            // response (fetched without an Origin header and without
            // Vary: Origin), which lacks CORS headers -> "Failed to fetch".
            const response = await fetch(downloadUrl, { cache: 'no-store' })
            if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`)

            const reader = response.body?.getReader()
            const contentLength = parseInt(response.headers.get('content-length') || '0', 10)
            let receivedLength = 0
            let chunks = []

            if (reader) {
              while (true) {
                const { done, value } = await reader.read()
                if (done) break

                chunks.push(value)
                receivedLength += value.length

                if (contentLength > 0) {
                  const progress = Math.round((receivedLength / contentLength) * 100)
                  dispatch(setIndividualProgress({ currentFileProgress: progress }))
                }
              }

              finalBlob = new Blob(chunks)
            } else {
              finalBlob = await response.blob()
              dispatch(setIndividualProgress({ currentFileProgress: 100 }))
            }
          }

          if (iosMultiBatch) {
            batchedShareFiles.push(new File([finalBlob], fileName, { type: finalBlob.type || "image/jpeg" }))
          } else if (isIOS() && typeof navigator.share === "function" && typeof navigator.canShare === "function") {
            const file = new File([finalBlob], fileName, { type: finalBlob.type || "image/jpeg" })
            if (navigator.canShare({ files: [file] })) {
              try {
                await navigator.share({ files: [file] })
              } catch (shareErr) {
                if (!(shareErr instanceof DOMException && shareErr.name === 'AbortError')) {
                  saveAs(finalBlob, fileName)
                }
              }
            } else {
              saveAs(finalBlob, fileName)
            }
          } else {
            saveAs(finalBlob, fileName)
          }

          if (i < filesRef.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500))
          }

        } catch (error) {
          const downloadUrl = eventData?.allowOriginalDownload ?
            getOptimalUrl(fileToDownload, false) :
            getOptimalUrl(fileToDownload, true)

          // TypeError here means a network/CORS failure from fetch() or stream reading.
          // Chrome: "Failed to fetch [hostname]", Safari: "Load failed [hostname]",
          // Firefox/others: "network error". The raw URL is often still reachable via
          // the browser's native download (no CORS restriction), so fall back gracefully.
          if (error instanceof TypeError) {
            toast.info("Opening file — long-press to save it to your device")
            window.location.href = downloadUrl
            continue
          }

          Sentry.captureException(error, {
            tags: { action: "download_file" },
            extra: { fileName: fileToDownload.name, downloadUrl },
          })
          console.error('Error downloading file:', { fileName: fileToDownload.name, downloadUrl, error })
          const reason = error instanceof Error ? error.message : String(error)
          toast.error(`Error downloading "${fileToDownload.name}": ${reason} | URL: ${downloadUrl}`)
          // Continue with next file instead of stopping
        }
      }

      // iOS batch: all blobs collected — fire one share sheet for all files.
      if (iosMultiBatch && batchedShareFiles.length > 0) {
        if (navigator.canShare({ files: batchedShareFiles })) {
          try {
            await navigator.share({ files: batchedShareFiles })
          } catch (shareErr) {
            if (!(shareErr instanceof DOMException && shareErr.name === 'AbortError')) {
              batchedShareFiles.forEach(f => saveAs(f, f.name))
            }
          }
        } else {
          batchedShareFiles.forEach(f => saveAs(f, f.name))
        }
      }

      toast.success(
        filesRef.length === 1
          ? "File Downloaded Successfully"
          : `${filesRef.length} Files Downloaded Successfully`
      )
    } catch (error) {
      Sentry.captureException(error, { tags: { action: "download_batch" } })
      toast.error("An error occurred during download. Please try again.")
    } finally {
      dispatch(setShowDownloadModal(false))
      dispatch(setIndividualProgress({
        currentFile: 0,
        totalFiles: 0,
        currentFileProgress: 0,
        currentFileName: '',
      }))
    }
  }

  const downloadFile = async (files: Array<MediaFile & { name: string; id?: string }>) => {
    const filesRef = files
    dispatch(resetSelectedPhotos())

    // In any WebView (WhatsApp/Instagram on iOS or Android), downloading multiple
    // files individually is unreliable — show the DownloadAll modal so the user
    // gets a zip link via email instead.
    if (isWebView() && filesRef.length > 1) {
      const ids = filesRef.map(f => f.id).filter((id): id is string => !!id)
      dispatch(setDownloadAllFileIds(ids.length === filesRef.length ? ids : null))
      dispatch(setShowDownloadAllModal(true))
      return
    }

    if (filesRef.length >= 1) {
      return downloadIndividualFiles(filesRef)
    }

    // ZIP DOWNLOAD FUNCTIONALITY COMMENTED OUT - Now using individual downloads for all multiple files
    /*
    } else {
      dispatch(setProgress(0))

      // Split files into chunks of chunkSize
      const parts = []
      for (let i = 0; i < filesRef.length; i += chunkSize) {
        parts.push(filesRef.slice(i, i + chunkSize))
      }

      dispatch(setTotalParts(parts.length))

      try {
        // Process each part separately
        for (let partNumber = 0; partNumber < parts.length; partNumber++) {
          // Get current chunk of files
          const mediaChunk = parts[partNumber]

          // Create a completely fresh zip for this chunk only
          const partZip = new JSZip()

          // Process files in this chunk
          for (let i = 0; i < mediaChunk.length; i++) {
            const { url, name, compressedUrl } = mediaChunk[i]
            const downloadUrl = eventData?.allowOriginalDownload ? url : (compressedUrl || url)

            let finalBlob: Blob
            let fileName = name || `file-${i}.jpg`

            // Check if watermark should be applied
            if (eventData?.watermarkEnabled && eventData?.watermarkText && isImage(downloadUrl) && !checkVideo(downloadUrl)) {
              try {
                // Generate watermarked image
                finalBlob = await generateWatermarkedImage(downloadUrl, eventData.watermarkText)
                // Ensure filename has proper extension
                if (!fileName.toLowerCase().includes('.jpg') && !fileName.toLowerCase().includes('.jpeg')) {
                  fileName = fileName.replace(/\.[^/.]+$/, '') + '.jpg'
                }
              } catch (error) {
                console.error('Error generating watermarked image:', error)
                // Fallback to original file
                const response = await fetch(downloadUrl)
                if (!response.ok) throw new Error(`Failed to fetch ${name}`)
                finalBlob = await response.blob()
              }
            } else {
              // Download original file
              const response = await fetch(downloadUrl)
              if (!response.ok) throw new Error(`Failed to fetch ${name}`)
              finalBlob = await response.blob()
            }

            partZip.file(fileName, finalBlob)

            // Update progress for current part
            dispatch(
              setProgress(Math.round(((i + 1) / mediaChunk.length) * 100))
            )
          }

          // Generate zip for this part only
          console.log(`Generating zip for part ${partNumber+1}/${parts.length}`)
          const zipBlob = await partZip.generateAsync({
            type: "blob",
            compression: "DEFLATE",
            compressionOptions: { level: 6 },
          })

          // Save this part's zip with Android fallback
          const fileName = `media-files-part${partNumber + 1}-of-${parts.length}.zip`

          try {
            const isAndroid = /Android/i.test(navigator.userAgent)

            if (isAndroid) {
              console.log(`Attempting Android download for ${fileName}`)

              // For Android, try multiple approaches
              const blobUrl = URL.createObjectURL(zipBlob)

              // Method 1: Traditional anchor download
              const anchor = document.createElement("a")
              anchor.href = blobUrl
              anchor.download = fileName
              anchor.style.display = "none"
              document.body.appendChild(anchor)
              anchor.click()
              document.body.removeChild(anchor)

              // Method 2: Also try saveAs as backup
              setTimeout(() => {
                try {
                  saveAs(zipBlob, fileName)
                  console.log(`saveAs called for ${fileName}`)
                } catch (e) {
                  console.log(`saveAs failed for ${fileName}:`, e)
                }
              }, 100)

              // Method 3: If still no download, open in new window
              setTimeout(() => {
                console.log(`Opening fallback window for ${fileName}`)
                window.open(blobUrl, '_blank')
              }, 1000)

              // Clean up URL after delay
              setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)

            } else {
              // For non-Android devices
              saveAs(zipBlob, fileName)
            }

            console.log(`Download triggered for ${fileName}, blob size: ${(zipBlob.size / 1024 / 1024).toFixed(2)}MB`)

          } catch (error) {
            console.error('Error downloading zip part:', error)
            // Final fallback - open in new window
            const blobUrl = URL.createObjectURL(zipBlob)
            window.open(blobUrl, '_blank')
          }

          // Reset progress and update current part
          dispatch(setProgress(0))
          dispatch(setCurrentPart(partNumber + 1))

          // Small delay before processing next part
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
        toast.success(`${filesRef.length} Files Downloaded Successfully`)
      } catch (error) {
        toast.error(
          "An error occurred during download. Please try again with fewer files."
        )
        setChunkSize(Math.max(2, chunkSize - 2))
      } finally {
        dispatch(setProgress(0))
        dispatch(setCurrentPart(0))
        dispatch(setTotalParts(0))
      }
    }
    */
  }

  // Opens the album picker modal for the given files. The actual add happens
  // in AddToAlbumModal via addFilesToAlbum once the host chooses an album.
  const moveToPhysicalAlbum = (photos: Array<string>) => {
    if (!photos?.length) return
    dispatch(setAddToAlbumArray(photos))
  }

  // Adds files to a specific physical album.
  const addFilesToAlbum = async (
    physicalAlbumId: string,
    photos: Array<string>
  ) => {
    try {
      const res = await updateFilesInPhysicalAlbum({
        userId: effectiveUserId,
        physicalAlbumId,
        fileIds: photos,
        addToPhysicalAlbum: true,
      })
      if (res?.status === "OK") {
        toast.success(
          `${photos?.length > 1 ? `${photos.length} Files` : "File"} Added Successfully`
        )
        dispatch(getPhysicalAlbums({ eventId: eventId || "", userId: userId || undefined }))
      } else {
        toast.error(res?.error || "Error Adding File To Album")
      }
    } catch (error) {
      Sentry.captureException(error, { tags: { action: "add_to_album" } })
      toast.error("Error Adding File To Album")
    } finally {
      dispatch(setAddToAlbumArray(null))
      dispatch(resetSelectedPhotos())
    }
  }

  const shareFile = {
    setSharable: (id: string) => {
      dispatch(setShareFile(id))
    },
    shareLink: () => {
      // Ensure the share link is an absolute URL for proper Open Graph tags rendering
      return `${window.location.origin}/${eventId}/file/${shareLink}`
    },
  }

  return {
    deleteFile,
    moveFile,
    downloadFile,
    downloadIndividualFiles,
    moveToPhysicalAlbum,
    addFilesToAlbum,
    shareFile,
  }
}
